import { useEffect, useRef } from "react";

// アセンション・リーダー段階で「あなたは選ばれた」などの光の文字が降り注ぐ Canvas
const RAIN_PHRASES = [
  "あなたは選ばれた",
  "CHOSEN",
  "導く者",
  "宇宙の意思",
  "ASCENSION",
  "選ばれし者",
  "光の中へ",
  "LEADER",
  "覚醒",
  "†",
  "∞",
  "次の時代",
  "COVENANT",
  "守護",
];

interface AscensionRainCanvasProps {
  active: boolean;
}

export default function AscensionRainCanvas({ active }: AscensionRainCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    interface RainText {
      x: number;
      y: number;
      vy: number;
      text: string;
      opacity: number;
      maxOpacity: number;
      size: number;
      hue: number; // 38〜55 金系
      phase: number;
      glowing: boolean;
    }

    const drops: RainText[] = [];

    const spawnDrop = () => {
      const text = RAIN_PHRASES[Math.floor(Math.random() * RAIN_PHRASES.length)];
      const isSingle = text.length <= 2;
      drops.push({
        x: Math.random() * canvas.width,
        y: -40,
        vy: 0.4 + Math.random() * 0.8,
        text,
        opacity: 0,
        maxOpacity: 0.08 + Math.random() * 0.18,
        size: isSingle ? 18 + Math.random() * 14 : 10 + Math.random() * 8,
        hue: 38 + Math.random() * 18,
        phase: Math.random() * Math.PI * 2,
        glowing: Math.random() < 0.25,
      });
    };

    // 初期配置
    for (let i = 0; i < 18; i++) {
      spawnDrop();
      drops[drops.length - 1].y = Math.random() * canvas.height;
    }

    let t = 0;
    let spawnTimer = 0;

    function draw() {
      t++;
      spawnTimer++;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 新しいドロップを生成
      if (spawnTimer > 40 && drops.length < 35) {
        spawnDrop();
        spawnTimer = 0;
      }

      for (let i = drops.length - 1; i >= 0; i--) {
        const d = drops[i];
        d.y += d.vy;
        d.phase += 0.015;

        // フェードイン/アウト
        const fadeZone = 80;
        if (d.y < fadeZone) {
          d.opacity = Math.min(d.maxOpacity, d.opacity + 0.003);
        } else if (d.y > canvas.height - fadeZone) {
          d.opacity = Math.max(0, d.opacity - 0.006);
        } else {
          d.opacity = Math.min(d.maxOpacity, d.opacity + 0.004);
        }

        if (d.y > canvas.height + 40) {
          drops.splice(i, 1);
          continue;
        }

        // 呼吸する透明度
        const breathAlpha = d.opacity * (0.7 + Math.sin(d.phase) * 0.3);

        ctx.save();
        ctx.font = `300 ${d.size}px 'Cormorant Garamond', serif`;
        ctx.textAlign = "center";

        if (d.glowing) {
          // グロー付き
          ctx.shadowColor = `hsla(${d.hue}, 80%, 70%, 0.8)`;
          ctx.shadowBlur = 12;
          ctx.fillStyle = `hsla(${d.hue}, 85%, 80%, ${breathAlpha * 1.4})`;
        } else {
          ctx.shadowBlur = 0;
          ctx.fillStyle = `hsla(${d.hue}, 70%, 72%, ${breathAlpha})`;
        }

        ctx.fillText(d.text, d.x, d.y);
        ctx.restore();
      }

      animRef.current = requestAnimationFrame(draw);
    }

    animRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [active]);

  if (!active) return null;

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{
        width: "100vw",
        height: "100vh",
        zIndex: 2,
        mixBlendMode: "screen",
      }}
    />
  );
}
